import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, BellRing } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScheduleForm, DEFAULT_SCHEDULE, toSchedulePayload } from "@/components/reports/ScheduleForm";
import { AlertMethodFields } from "@/components/alerts/AlertMethodFields";
import { METHODS, DEFAULT_CONFIG } from "@/lib/alerts";
import { ALERTS } from "@/constants/testIds";
import { detectTimezone } from "@/lib/cron";
import api from "@/lib/api";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export function WatchMetricDialog({ open, onOpenChange, prompt, plan, workspaceId }) {
  const navigate = useNavigate();
  const [name, setName] = useState((prompt || "Untitled alert").slice(0, 120));
  const [method, setMethod] = useState("threshold");
  const [config, setConfig] = useState(DEFAULT_CONFIG.threshold);
  const [severity, setSeverity] = useState("auto");
  const [cooldown, setCooldown] = useState(30);
  const [schedule, setSchedule] = useState({ ...DEFAULT_SCHEDULE, cron: "0 * * * *", timezone: detectTimezone() });
  const [saving, setSaving] = useState(false);

  const changeMethod = (m) => { setMethod(m); setConfig(DEFAULT_CONFIG[m]); };

  const save = async () => {
    if (!name.trim() || !workspaceId) return;
    setSaving(true);
    try {
      const sp = toSchedulePayload(schedule);
      const body = {
        workspace_id: workspaceId,
        name: name.trim(),
        prompt: prompt || "",
        plan_frozen: plan || {},
        method,
        config,
        cron: sp.cron,
        timezone: sp.timezone,
        delivery: sp.delivery,
        recipients: sp.recipients,
        slack_webhook_url: sp.slack_webhook_url,
        webhook_url: sp.webhook_url,
        severity_override: severity === "auto" ? null : severity,
        cooldown_minutes: Number(cooldown) || 30,
      };
      const { data } = await api.post("/alerts", body);
      toast.success("Alert created", {
        description: "Sema will watch this metric and ping you on change.",
        action: { label: "Open", onClick: () => navigate(`/alerts/${data.id}`) },
      });
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't create alert", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={ALERTS.watchDialog} className="max-h-[90vh] overflow-y-auto font-mono sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-accent" /> Watch this metric
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sema re-runs this metric on a schedule and pings you when it deviates beyond the rule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Field label="Alert name">
            <Input data-testid={ALERTS.watchName} value={name} onChange={(e) => setName(e.target.value)} className="bg-card/60" />
          </Field>

          <Field label="Detection method">
            <Select value={method} onValueChange={changeMethod}>
              <SelectTrigger data-testid={ALERTS.watchMethod} className="bg-card/60"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">{METHODS.find((m) => m.value === method)?.hint}</p>
          </Field>

          <div className="rounded-lg border border-border/60 bg-card/30 p-3">
            <AlertMethodFields method={method} config={config} onChange={setConfig} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Severity">
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid={ALERTS.severityOverride} className="bg-card/60"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (from deviation)</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cooldown (min)">
              <Input data-testid={ALERTS.cooldown} type="number" value={cooldown}
                onChange={(e) => setCooldown(e.target.value)} className="bg-card/60" />
            </Field>
          </div>

          <div className="border-t border-border/40 pt-4">
            <p className="mb-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Schedule & delivery</p>
            <ScheduleForm value={schedule} onChange={setSchedule} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid={ALERTS.watchSubmit} onClick={save} disabled={saving || !name.trim()}
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create alert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
